/**
 * Module: screens/creator/steps/UrlStep
 * Role: Wizard Step 1/8 — collect VK URL and/or TikTok URL (FTR-1106 +
 *       FTR-1131 chrome rewrite). At least one URL required.
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - screens/creator/store:useWizardStore
 *   - screens/creator/_chrome/WizardShell
 *   - screens/flow/atoms/HintBar
 *   - styles/tokens:P, T
 *
 * Used by: CreatorOnboardingScreen.tsx
 * Glossary: ai/glossary/buyer.md
 *
 * ADR-023: no window.Telegram access, no platform literals
 * ADR-022 §7: no openExternal(BOT_LINK)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizardStore } from '../store';
import { HintBar } from '../../flow/atoms/HintBar';
import { P, T } from '../../../styles/tokens';
import {
  WizardBody, WizardCtaBar, WizardFrame, WizardHeader, WizardStepStrip,
} from '../_chrome/WizardShell';

// Lenient normalization — accept what a human naturally types.
function normalizeUrl(raw: string): string | null {
  let v = raw.trim();
  if (!v) return null;
  v = v.replace(/^@+/, '').replace(/^\/+/, '');
  if (!/^https?:\/\//i.test(v)) v = `https://${v}`;
  try {
    const u = new URL(v);
    if (!u.hostname.includes('.')) return null;
    return u.toString();
  } catch {
    return null;
  }
}

export default function UrlStep() {
  const navigate = useNavigate();
  const { draft, setUrl } = useWizardStore();

  const [vkUrl, setVkUrl] = useState(draft.vk_url);
  const [tiktokUrl, setTiktokUrl] = useState(draft.tiktok_url);
  const [error, setError] = useState<string | null>(null);

  function handleNext() {
    const vkTrimmed = vkUrl.trim();
    const tiktokTrimmed = tiktokUrl.trim();

    if (!vkTrimmed && !tiktokTrimmed) {
      setError('Введите ссылку на VK или TikTok');
      return;
    }
    const vkNorm = vkTrimmed ? normalizeUrl(vkTrimmed) : '';
    const tiktokNorm = tiktokTrimmed ? normalizeUrl(tiktokTrimmed) : '';
    if (vkTrimmed && vkNorm === null) {
      setError('Неверный формат VK-ссылки'); return;
    }
    if (tiktokTrimmed && tiktokNorm === null) {
      setError('Неверный формат TikTok-ссылки'); return;
    }

    setError(null);
    setUrl(vkNorm || '', tiktokNorm || '');
    navigate('/creator-onboarding/selfie');
  }

  // Back to promo (design L497: `if (step === 'url') go('promo')`).
  const handleBack = () => navigate('/creator-onboarding/promo');

  const inputStyle = {
    width: '100%',
    padding: '13px 14px',
    borderRadius: 12,
    border: `1.5px solid ${P.line}`,
    font: T.callout,
    color: P.ink,
    // Force ink caret — default OS caret renders white inside TG WebView dark
    // theme (founder report 2026-06-07 round 3).
    caretColor: P.ink,
    background: P.card,
    outline: 'none',
    boxSizing: 'border-box' as const,
  };

  return (
    <WizardFrame>
      <WizardHeader title="Верификация" onBack={handleBack} />
      <WizardStepStrip current={1} total={8} />
      <WizardBody>
        <div style={{ paddingTop: 10 }}>
          <div style={{ font: T.title2, color: P.ink, marginBottom: 4 }}>
            Ваши социальные сети
          </div>
          <div style={{ font: T.footnote, color: P.inkSoft }}>
            Укажите ссылку на VK или TikTok для проверки аудитории
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label
              style={{ font: T.footnote, color: P.inkSoft, display: 'block', marginBottom: 6 }}
            >
              VK-профиль или группа
            </label>
            <input
              type="url"
              placeholder="https://vk.com/yourprofile"
              value={vkUrl}
              onChange={(e) => { setVkUrl(e.target.value); setError(null); }}
              style={inputStyle}
              autoComplete="off"
              autoCapitalize="none"
            />
          </div>

          <div>
            <label
              style={{ font: T.footnote, color: P.inkSoft, display: 'block', marginBottom: 6 }}
            >
              TikTok-профиль
            </label>
            <input
              type="url"
              placeholder="https://tiktok.com/@yourprofile"
              value={tiktokUrl}
              onChange={(e) => { setTiktokUrl(e.target.value); setError(null); }}
              style={inputStyle}
              autoComplete="off"
              autoCapitalize="none"
            />
          </div>
        </div>

        {error && <HintBar>{error}</HintBar>}
      </WizardBody>
      <WizardCtaBar label="Далее" onClick={handleNext} />
    </WizardFrame>
  );
}
